/* Robles Veterinaria: conserva el endpoint y los resultados nativos de Shopify. */
class PredictiveSearch extends HTMLElement {
  static cache = new Map();
  static instanceCount = 0;

  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.instanceId = `rv-search-${++PredictiveSearch.instanceCount}`;
    this.searchTerm = '';
    this.searchActive = false;
    this.isOpen = false;
    this.mobileViewport = window.matchMedia('(max-width: 1024px)');
    this.onViewportChange = this.updateMaxHeight.bind(this);
    this.onOutsideClick = (event) => { if (!this.contains(event.target)) this.close(); };
    this.setupClearButton();
    this.setupEventListeners();
  }

  setupClearButton() {
    this.input.closest('.field').classList.add('rvps__field');
    this.clearButton = document.createElement('button');
    this.clearButton.type = 'button';
    this.clearButton.className = 'rvps__clear';
    this.clearButton.setAttribute('aria-label', 'Borrar búsqueda y cerrar resultados');
    this.clearButton.innerHTML = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="m5 5 10 10M15 5 5 15"/></svg>';
    this.input.after(this.clearButton);
    this.updateClearButton();
    this.clearButton.addEventListener('click', (event) => {
      this.close(true);
      // Al tocar la tachita también se oculta el teclado; con teclado físico se conserva el foco.
      if (event.detail === 0) this.input.focus({ preventScroll: true });
      else this.input.blur();
      this.setLiveRegionText('Búsqueda borrada.');
    });
  }

  updateClearButton() { this.clearButton.hidden = !this.input.value; }

  updateMobileBackdrop() {
    const active = this.isOpen && this.mobileViewport.matches && this.getClientRects().length > 0;
    if (!active) { this.hideMobileBackdrop(); return; }
    if (!this.backdrop) {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'rvps__backdrop';
      this.backdrop.setAttribute('aria-hidden', 'true');
      // Espera al clic completo para que el toque no alcance la página de fondo.
      this.backdrop.addEventListener('pointerdown', (event) => event.preventDefault());
      this.backdrop.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.input.blur();
      });
      this.prepend(this.backdrop);
    }
    if (!this.hasAttribute('data-rvps-mobile-open')) {
      this.backdropHost = this.closest('.shopify-section');
      this.backdropHeader = this.closest('.section-header') || document.querySelector('.section-header');
      if (this.backdropHost) {
        this.backdropHost.classList.toggle('rvps-mobile-host-static', getComputedStyle(this.backdropHost).position === 'static');
        this.backdropHost.classList.add('rvps-mobile-host');
      }
      this.setAttribute('data-rvps-mobile-open', '');
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
    }
    // El efecto comienza debajo del encabezado, incluso al cambiar la altura del teclado.
    const headerBottom = (this.backdropHeader || this.input.closest('.field')).getBoundingClientRect().bottom;
    this.backdrop.style.top = `${Math.max(0, headerBottom)}px`;
  }

  hideMobileBackdrop() {
    this.removeAttribute('data-rvps-mobile-open');
    this.backdropHost?.classList.remove('rvps-mobile-host', 'rvps-mobile-host-static');
    this.backdropHost = null;
    this.backdropHeader = null;
    window.removeEventListener('scroll', this.onViewportChange);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', (event) => {
      if (!this.getQuery()) event.preventDefault();
    });
    this.input.addEventListener('input', () => {
      clearTimeout(this.inputTimer);
      this.abortController?.abort();
      this.clearSelection();
      this.updateClearButton();
      if (!this.getQuery()) { this.close(true); return; }
      this.searchActive = true;
      document.addEventListener('pointerdown', this.onOutsideClick);
      this.inputTimer = setTimeout(() => this.onChange(), 300);
    });
    this.input.addEventListener('focus', () => this.onFocus());
    this.addEventListener('focusout', (event) => {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        const active = document.activeElement;
        if (this.contains(active)) return;
        // La palomita del teclado puede quitar el foco sin tocar otro elemento.
        const neutralFocus = !active || active === document.body || active === document.documentElement;
        const touchInput = window.matchMedia('(any-pointer: coarse)').matches;
        if (this.searchActive && event.target === this.input && !event.relatedTarget && neutralFocus && touchInput && this.getQuery()) {
          this.updateMaxHeight();
          return;
        }
        this.close();
      }, 0);
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rvps-toggle]');
      if (!button) return;
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
      button.closest('.rvps__suggestions').toggleAttribute('data-expanded', expanded);
      this.clearSelection();
      this.updateMaxHeight();
    });
  }

  disconnectedCallback() {
    this.close();
    clearTimeout(this.focusTimer);
    clearTimeout(this.statusTimer);
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    this.searchTerm = this.getQuery();
    if (!this.searchTerm) { this.close(true); return; }
    this.getSearchResults(this.searchTerm);
  }

  onFocus() {
    this.updateClearButton();
    document.querySelectorAll('predictive-search').forEach((search) => {
      if (search !== this && (search.searchActive || search.isOpen)) search.close();
    });
    this.searchActive = true;
    document.addEventListener('pointerdown', this.onOutsideClick);
    if (!this.getQuery()) return;
    if (this.renderedTerm === this.getQuery() && this.hasAttribute('results')) this.open();
    else this.onChange();
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      if (this.isOpen) { event.preventDefault(); event.stopPropagation(); this.input.focus(); this.close(); }
      return;
    }
    if (event.target !== this.input) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isOpen && this.hasAttribute('results') && this.renderedTerm === this.getQuery()) this.open();
      this.switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
    } else if (event.key === 'Enter' && this.isOpen && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
  }

  clearSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }

  switchOption(direction) {
    if (!this.isOpen) return;
    const options = Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter((option) => option.getClientRects().length);
    if (!options.length) return;
    const previous = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const next = direction === 'up' ? (previous < 1 ? options.length - 1 : previous - 1) : (previous + 1) % options.length;
    this.clearSelection();
    const option = options[next];
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    // Desplaza únicamente el panel; la página y su encabezado permanecen quietos.
    const panel = this.predictiveSearchResults;
    const bounds = panel.getBoundingClientRect();
    const item = option.getBoundingClientRect();
    const footer = this.querySelector('.rvps__footer');
    const bottom = bounds.bottom - (option === footer ? 0 : (footer?.offsetHeight || 0));
    if (item.top < bounds.top) panel.scrollTop += item.top - bounds.top;
    else if (item.bottom > bottom) panel.scrollTop += item.bottom - bottom;
  }

  selectOption() { this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')?.click(); }

  async getSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const controller = this.abortController;
    const cacheKey = `${routes.predictive_search_url}:${searchTerm}`;
    this.setLiveRegionLoadingState();
    try {
      let markup = PredictiveSearch.cache.get(cacheKey);
      if (!markup) {
        const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, { signal: controller.signal });
        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = document.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search: missing section');
        markup = section.innerHTML;
        PredictiveSearch.cache.set(cacheKey, markup);
        if (PredictiveSearch.cache.size > 40) PredictiveSearch.cache.delete(PredictiveSearch.cache.keys().next().value);
      }
      if (controller.signal.aborted || this.getQuery() !== searchTerm || !this.searchActive) return;
      this.renderSearchResults(markup);
    } catch (error) {
      if (controller.signal.aborted) return;
      this.close();
      // El formulario normal sigue disponible cuando falla la consulta predictiva.
      console.warn('No se pudieron cargar las sugerencias de búsqueda.', error);
    }
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    clearTimeout(this.statusTimer);
    this.statusElement.textContent = text;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusTimer = setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  setLiveRegionLoadingState() {
    this.setLiveRegionText(this.getAttribute('data-loading-text') || 'Cargando');
    this.setAttribute('loading', 'true');
    this.input.setAttribute('aria-busy', 'true');
  }

  renderSearchResults(markup) {
    this.clearSelection();
    this.predictiveSearchResults.innerHTML = markup;
    // El encabezado puede contener buscadores separados para móvil y escritorio.
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      ids.set(element.id, `${this.instanceId}-${element.id}`);
      element.id = ids.get(element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-controls]').forEach((element) => {
      ['aria-labelledby', 'aria-controls'].forEach((attribute) => {
        if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
      });
    });
    const resultsId = ids.get('predictive-search-results');
    if (resultsId) { this.input.setAttribute('aria-controls', resultsId); this.input.setAttribute('aria-owns', resultsId); }
    this.setAttribute('results', 'true');
    this.renderedTerm = this.getQuery();
    this.predictiveSearchResults.scrollTop = 0;
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.input.removeAttribute('aria-busy');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent.trim() || '');
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    this.resultsMaxHeight = Math.max(0, Math.min(640, bottom - this.predictiveSearchResults.getBoundingClientRect().top - 12));
    return this.resultsMaxHeight;
  }

  updateMaxHeight() {
    if (!this.isOpen) return;
    this.updateMobileBackdrop();
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  open() {
    this.searchActive = true;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.updateMaxHeight();
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', this.onViewportChange, { passive: true });
    document.addEventListener('pointerdown', this.onOutsideClick);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.searchActive = false;
    this.hideMobileBackdrop();
    clearTimeout(this.inputTimer);
    this.abortController?.abort();
    if (clearSearchTerm) { this.input.value = ''; this.searchTerm = ''; this.removeAttribute('results'); }
    this.updateClearButton();
    this.clearSelection();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.removeAttribute('aria-busy');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.style.removeProperty('max-height');
    window.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
    document.removeEventListener('pointerdown', this.onOutsideClick);
  }
}
customElements.define('predictive-search', PredictiveSearch);
